// ABOUTME: Country multi-select constrained to a pool (e.g. a return profile's allowed countries).
// ABOUTME: Index-based InputDropdown over the pool plus region quick-selects (Benelux/Europa/...) narrowed to the pool.

import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, ButtonBase, useTheme } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import cx from "classnames";

import { tokens } from "../../theme";
import { countriesFromLocale, quickSelect } from "../../utils/countries";
import { InputDropdown } from "../theme/dropdowns/InputDropdown";

// props: countryPool (alpha2 codes allowed), selected (alpha2 codes), onChange(codes)
const CountryPoolSelector = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const language = useSelector((state) => state.settings.language);

    const pool = useMemo(
        () => (props.countryPool ?? []).map((code) => String(code).toLowerCase()),
        [props.countryPool]
    );

    // The selectable pool, as country objects, in locale order.
    const poolCountries = useMemo(
        () => countriesFromLocale(language).filter((c) => pool.includes(c.alpha2)),
        [language, pool]
    );

    const selected = (props.selected ?? []).map((code) => String(code).toLowerCase());

    // InputDropdown works in indices into poolCountries.
    const selectedIndices = selected
        .map((code) => poolCountries.findIndex((c) => c.alpha2 === code))
        .filter((i) => i !== -1);

    const handleCountryChange = (indices) => props.onChange(indices.map((i) => poolCountries[i].alpha2));

    // Only offer a region the pool can fully satisfy — a NL+BE pool shouldn't
    // show "Europa"/"EU" when it doesn't contain all those countries.
    const regions = useMemo(
        () =>
            quickSelect
                .filter((region) => region.countries.every((code) => pool.includes(code)))
                .map((region) => ({ name: region.name, codes: region.countries })),
        [pool]
    );

    const isRegionActive = (region) =>
        region.codes.length === selected.length && region.codes.every((code) => selected.includes(code));

    // Clicking a region sets exactly its (pool-narrowed) countries; clicking the active one clears.
    const applyRegion = (region) => props.onChange(isRegionActive(region) ? [] : region.codes);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <InputDropdown
                multiple={true}
                allowSelectAll
                noSelection={t('choose_countries')}
                displayName="name"
                searchOptions={{ enabled: true, placeholder: t("search") }}
                options={poolCountries}
                selected={selectedIndices}
                onChange={handleCountryChange}
                closeOnSelection={false}
            />

            {regions.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {regions.map((region) => (
                        <ButtonBase
                            key={region.name}
                            onClick={() => applyRegion(region)}
                            className={cx(undefined, { "is-active": isRegionActive(region) })}
                            sx={{
                                px: 1.25,
                                height: 26,
                                gap: 0.5,
                                borderRadius: 4,
                                fontSize: 12,
                                fontWeight: 600,
                                fontFamily: "Roboto, sans-serif",
                                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                color: colors.txt["secondary"],
                                "&:hover": { background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200] },
                                "&.is-active": {
                                    background: theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                    color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                },
                            }}
                        >
                            {region.name}
                            {isRegionActive(region) && <CheckIcon sx={{ fontSize: 14 }} />}
                        </ButtonBase>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default CountryPoolSelector;
